import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Card, Field, Input, Toggle } from "@aigntx/ui";
import { useAuth } from "../auth";
import { useTenant } from "../tenant";
import { ThemeControls } from "../ThemeControls";
import { BrandMark } from "../BrandMark";
import { TestSignIn } from "../components/TestSignIn";

export function LoginPage() {
  const { login, user } = useAuth();
  const tenant = useTenant();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to="/" replace />;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password, remember);
      navigate("/");
    } catch {
      setError(t("login.error"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth">
      <div className="auth__top">
        <ThemeControls />
      </div>
      <Card className="auth__card">
        <div className="auth__brand">
          <BrandMark icon={tenant.theme.icon} size={34} />
          <span className="brand__name">{tenant.name}</span>
        </div>
        <h1 className="auth__title">{t("login.title")}</h1>
        <p className="auth__sub">{t("login.subtitle", { tenant: tenant.name })}</p>
        <form className="auth__form" onSubmit={submit}>
          <Field label={t("login.email")} htmlFor="email">
            <Input
              id="email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </Field>
          <Field label={t("login.password")} htmlFor="password" error={error || undefined}>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              invalid={Boolean(error)}
              required
            />
          </Field>
          <div className="auth__row">
            <Toggle checked={remember} onChange={setRemember} label={t("login.remember")} id="remember" />
            <Link className="auth__link" to="/forgot">
              {t("login.forgot")}
            </Link>
          </div>
          <Button type="submit" block loading={busy}>
            {busy ? t("login.signingIn") : t("login.submit")}
          </Button>
        </form>
        <TestSignIn
          slug={tenant.slug}
          onPick={(e, p) => {
            setEmail(e);
            setPassword(p);
            setError("");
          }}
        />
        <div className="auth__legal">
          <Link to="/terms">{t("legal.terms")}</Link>
          <span aria-hidden="true">·</span>
          <Link to="/privacy">{t("legal.privacy")}</Link>
        </div>
      </Card>
    </div>
  );
}
